import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import Button from '../../../form/Button';
import Table from '../../../table/Table';
import type { TableProps } from '../../../table/Table';
import { format } from '../../../../utils/formatting';
import type { AttributeEntityType, AttributeEntry } from '../contracts';
import { assertNeverAttributeEntry, formatAttributeValueType } from '../contracts';
import { createAttributeEditPath } from '../routeUtils';
import { getAttributeDisplayLabels } from '../builtInLabels';

interface AttributesOverviewTableProps {
    entityType: AttributeEntityType;
    entityId: string;
    entries: AttributeEntry[];
    loading: boolean;
    canEdit: boolean;
}

type AttributeOverviewRow = {
    label: string;
    valueTypeLabel: string;
    valuePreview: ReactNode;
    assetsLabel: string;
    updatedAtLabel: string;
    editUrl: string;
};

type AttributeOverviewColumn = TableProps<AttributeOverviewRow>['columns'][number];

function getValuePreview(entry: AttributeEntry): ReactNode {
    if (entry.valueType === 'text') {
        return entry.textValue.trim() ? (
            <span className="line-clamp-2">{entry.textValue}</span>
        ) : (
            <span className="text-gray-400">{gettext('ATTRIBUTE_EMPTY_VALUE')}</span>
        );
    }

    if (entry.valueType === 'image') {
        if (!entry.image) {
            return <span className="text-gray-400">{gettext('ATTRIBUTE_NO_IMAGE')}</span>;
        }

        return (
            <div className="flex items-center gap-3">
                <img
                    src={entry.image.url}
                    alt=""
                    className="h-10 w-14 rounded-md object-cover ring-1 ring-gray-200"
                />
                <span className="line-clamp-2">{entry.image.caption || entry.image.name}</span>
            </div>
        );
    }

    if (entry.valueType === 'image_list') {
        if (entry.imageList.length === 0) {
            return <span className="text-gray-400">{gettext('ATTRIBUTE_NO_IMAGES')}</span>;
        }

        return (
            <div className="flex items-center gap-2">
                {entry.imageList.slice(0, 3).map((asset) => (
                    <img
                        key={asset.id}
                        src={asset.url}
                        alt=""
                        className="h-10 w-10 rounded-md object-cover ring-1 ring-gray-200"
                    />
                ))}
                {entry.imageList.length > 3 && (
                    <span className="text-xs text-gray-500">+{entry.imageList.length - 3}</span>
                )}
            </div>
        );
    }

    return assertNeverAttributeEntry(entry);
}

function getAssetsLabel(entry: AttributeEntry): string {
    if (entry.valueType === 'text') {
        return '-';
    }

    if (entry.valueType === 'image') {
        return String(entry.image ? 1 : 0);
    }

    if (entry.valueType === 'image_list') {
        return String(entry.imageList.length);
    }

    return assertNeverAttributeEntry(entry);
}

function formatUpdatedAt(updatedAt: string | null): string {
    if (!updatedAt) {
        return gettext('ATTRIBUTE_NOT_SAVED_YET');
    }

    return format(updatedAt, { useBrowserTimezone: true, hideSeconds: true });
}

export function AttributesOverviewTable({
    entityType,
    entityId,
    entries,
    loading,
    canEdit,
}: AttributesOverviewTableProps) {
    const rows = useMemo<AttributeOverviewRow[]>(() => {
        const labels = getAttributeDisplayLabels(entries, (entry) => entry.label);

        return entries.map((entry, index) => ({
            label: labels[index],
            valueTypeLabel: formatAttributeValueType(entry.valueType),
            valuePreview: getValuePreview(entry),
            assetsLabel: getAssetsLabel(entry),
            updatedAtLabel: formatUpdatedAt(entry.updatedAt),
            editUrl: createAttributeEditPath(entityType, entityId, entry.definitionId),
        }));
    }, [entityId, entityType, entries]);

    const columns = useMemo<AttributeOverviewColumn[]>(
        () => [
            {
                key: 'label',
                title: gettext('ATTRIBUTE_LABEL_FIELD'),
                render: (_column, row) => (
                    <div className="flex flex-col">
                        {canEdit ? (
                            <Link
                                to={row.editUrl}
                                state={{ fromAttributesOverview: true }}
                                className="font-semibold text-gray-900 hover:underline"
                            >
                                {row.label}
                            </Link>
                        ) : (
                            <span className="font-semibold text-gray-500">{row.label}</span>
                        )}
                    </div>
                ),
            },
            {
                key: 'valueTypeLabel',
                title: gettext('ATTRIBUTE_TYPE_FIELD'),
                render: (_column, row) => (
                    <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                        {row.valueTypeLabel}
                    </span>
                ),
            },
            {
                key: 'valuePreview',
                title: gettext('ATTRIBUTE_VALUE_PREVIEW_COLUMN'),
            },
            {
                key: 'assetsLabel',
                title: gettext('ATTRIBUTE_ASSETS_COLUMN'),
            },
            {
                key: 'updatedAtLabel',
                title: gettext('ATTRIBUTE_UPDATED_AT_COLUMN'),
            },
            {
                key: 'actions',
                title: gettext('ATTRIBUTE_ACTION_COLUMN'),
                render: (_column, row) =>
                    canEdit ? (
                        <Button
                            to={row.editUrl}
                            state={{ fromAttributesOverview: true }}
                            variant="primary"
                            className="inline-flex px-5 py-2 text-sm"
                        >
                            {gettext('ATTRIBUTE_EDIT_ACTION')}
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="primary"
                            className="cursor-not-allowed px-5 py-2 text-sm opacity-50"
                            disabled
                        >
                            {gettext('ATTRIBUTE_EDIT_ACTION')}
                        </Button>
                    ),
            },
        ],
        [canEdit]
    );

    return (
        <Table
            data={rows}
            columns={columns}
            loading={loading}
            showDivider={false}
            showBorder={false}
            wrapperClasses="overflow-x-auto rounded-b-2xl"
            headerRowClasses="bg-gray-50 text-left text-xs font-semibold tracking-wide text-gray-500"
            headerCellClasses="px-6 py-3"
            rowClasses="border-t border-gray-100 align-top"
            rowCellClasses="px-6 py-4 text-sm text-gray-800"
        />
    );
}
